import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Typography } from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./UserInfoForm.css";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [otpTimer, setOtpTimer] = useState(300); // 5 minutes in seconds
  const navigate = useNavigate();

  useEffect(() => {
    let interval;
    if (otpSent && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (otpTimer === 0) {
      setOtpSent(false);
      setErrorMessage("OTP expired. Please request a new one.");
    }
    return () => clearInterval(interval);
  }, [otpSent, otpTimer]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleSendOtp = async () => {
    try {
      const trimmedEmail = email.trim();
      // Double encode the email to handle special characters
      // const encodedEmail = encodeURIComponent(trimmedEmail).replace(
      //   "+",
      //   /%2B/g
      // );

      // First check if email exists in database
      const userResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/email/${trimmedEmail}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!userResponse.data || !userResponse.data.id) {
        setErrorMessage("Email not found.");
        return;
      }

      // If email exists, proceed with sending OTP
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/send-otp`,
        {
          email: trimmedEmail, // Use original email in body
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setOtpSent(true);
        setOtpTimer(300); // Reset timer to 5 minutes
        setErrorMessage("");
      } else {
        setErrorMessage(
          response.data.message || "Failed to send OTP. Please try again."
        );
      }
    } catch (error) {
      console.error("Error:", error);
      if (error.response) {
        // Handle specific error responses
        if (error.response.status === 404) {
          setErrorMessage("Email not found.");
        } else if (error.response.data && error.response.data.message) {
          setErrorMessage(error.response.data.message);
        } else {
          setErrorMessage("Error sending OTP. Please try again.");
        }
      } else if (error.request) {
        // Handle network errors
        setErrorMessage(
          "Network error. Please check your connection and try again."
        );
      } else {
        setErrorMessage("Error sending OTP. Please try again.");
      }
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/verify-otp`,
        { email, otp }
      );
      if (response.data.success) {
        // Fetch user information
        // const encodedEmail = encodeURIComponent(email);
        const trimmedEmail = email.trim();
        const userResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/email/${trimmedEmail}`
        );
        if (userResponse.data && userResponse.data.id) {
          localStorage.setItem("userId", userResponse.data.id);
          localStorage.setItem("answersSubmitted", "true");
          navigate(`/dashboard/${userResponse.data.id}`);
        } else {
          setErrorMessage("User not found. Please try again.");
        }
      } else {
        setErrorMessage("Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setErrorMessage("Error verifying OTP. Please try again.");
    }
  };

  return (
    <Box className="user-info-form-container">
      <form className="user-info-form" onSubmit={(e) => e.preventDefault()}>
        <Typography variant="h5">Welcome Back!</Typography>
        <Typography variant="subtitle1">
          Please enter your email to access your dashboard.
        </Typography>
        <TextField
          label="Email"
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          fullWidth
          margin="normal"
          disabled={otpSent}
        />
        {otpSent && (
          <>
            <TextField
              label="OTP"
              variant="outlined"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              fullWidth
              margin="normal"
            />
            <Typography variant="body2" color="textSecondary">
              OTP valid for: {formatTime(otpTimer)}
            </Typography>
          </>
        )}
        {errorMessage && <Typography color="error">{errorMessage}</Typography>}
        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={otpSent ? handleVerifyOtp : handleSendOtp}
          disabled={otpSent && otpTimer === 0}
        >
          {otpSent ? "Verify OTP" : "Send OTP"}
        </Button>
      </form>
    </Box>
  );
};

export default LoginPage;
